import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, ThemeSync } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { DivisionProvider } from './contexts/DivisionContext';
import { SocketProvider } from './contexts/SocketContext';
import { LanguageProvider, LanguageSync } from './contexts/LanguageContext';
import { initI18n } from './i18n';
import { NotificationSoundProvider } from './contexts/NotificationSoundContext';
import { ChatProvider } from './components/messaging/ChatProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ObservabilitySync } from './components/ObservabilitySync';
import { initBugsnag } from './lib/bugsnag';
import { initAnalytics } from './lib/analytics';
import App from './App';
import './styles/index.css';

// Before any other module does work, so an error thrown during i18n load or
// the initial render is still reported. Both are no-ops when their build-time
// keys are absent, which is the normal state of a local build.
initBugsnag();
initAnalytics();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

// Auth + Socket depend on router context (their consumers use useNavigate /
// useLocation), so they live inside the data router. Theme / Query /
// ErrorBoundary stay outside. <ThemeSync /> sits inside AuthProvider so it
// can read the current user; it doesn't render anything.
function AppShell() {
  return (
    <AuthProvider>
      <ThemeSync />
      <LanguageSync />
      <ObservabilitySync />
      <DivisionProvider>
        <SocketProvider>
          <ChatProvider>
            <NotificationSoundProvider>
              <App />
            </NotificationSoundProvider>
          </ChatProvider>
        </SocketProvider>
      </DivisionProvider>
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  { path: '*', element: <AppShell /> },
]);

initI18n()
  .catch((err) => console.error('[i18n] init failed, rendering with defaults', err))
  .then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <ErrorBoundary>
          <LanguageProvider>
            <ThemeProvider>
              <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
              </QueryClientProvider>
            </ThemeProvider>
          </LanguageProvider>
        </ErrorBoundary>
      </React.StrictMode>
    );
  });
